// #336 S1: each round of a slot has its own list of sets. The Draft keeps one
// row per set position; a round with fewer sets marks its last rows absent
// (CellDraft.absent, read through isCellAbsent). These are the slot-level
// moves that keep that true: count, add, remove, and the superset rule (Q7).
// Pure, on one slot or one day's slots. Imports only TYPES from ./programEdit,
// so it stays out of the runtime import cycle (see programEditSeedRow).
import type { AuthoredText } from "./authoredText.js";
import type { CellDraft, SetRowDraft, SlotDraft } from "./programEdit.js";
import { isCellAbsent, kindAt } from "./programEditBuild.js";
import { seedNextSetRow } from "./programEditSeedRow.js";

/** A set this round does not have (S1). */
export const absentCell = (): CellDraft => ({ reps: "0", intensityValue: "", absent: true });

/** The sets slot runs in round `w`: 0 when it does not run there. */
export const setCountAt = (slot: SlotDraft, w: number): number =>
  slot.inactiveWeeks[w] ? 0 : slot.setRows.filter((r) => !isCellAbsent(r, w)).length;

/** S1: the rounds the slot runs in do not all have the same number of sets. */
export const hasPerRoundSetCounts = (slot: SlotDraft): boolean => {
  const counts = new Set<number>();
  slot.inactiveWeeks.forEach((off, w) => {
    if (!off) counts.add(setCountAt(slot, w));
  });
  return counts.size > 1;
};

/** `cell`, read in row `from`, written into row `to`: its axis travels. */
const carry = (cell: CellDraft, from: SetRowDraft, to: SetRowDraft, w: number): CellDraft => {
  const rest: CellDraft = { ...cell };
  delete rest.kind;
  delete rest.absent;
  const kind = kindAt(from, w);
  return kind === to.intensityKind ? rest : { ...rest, kind };
};

/** The indices of the slots in superset `groupId`. The one answer for every
 *  per-member write (alignGroupSlots, the lens, prescribeDispatch). */
export const groupMemberIdxs = (slots: readonly SlotDraft[], groupId: string): number[] =>
  slots.flatMap((s, i) => (s.group?.id === groupId ? [i] : []));

/** Row `row` with round `w` replaced: the cell, its note and stored note. */
const putAt = (
  row: SetRowDraft,
  w: number,
  cell: CellDraft,
  note = "",
  raw?: AuthoredText,
): SetRowDraft => {
  const raws = row.notesRawPerCell ?? (raw !== undefined ? row.cells.map(() => undefined) : undefined);
  return {
    ...row,
    cells: row.cells.map((c, i) => (i === w ? cell : c)),
    notesPerCell: row.notesPerCell.map((n, i) => (i === w ? note : n)),
    ...(raws ? { notesRawPerCell: raws.map((n, i) => (i === w ? raw : n)) } : {}),
  };
};

/** Round `w` of row `to` takes the set read at round `w` of row `from`: its
 *  values, axis and note. The bound (reps or time) is on the ROW, so it
 *  travels only when `to` runs in no other round; otherwise two different
 *  bounds refuse (null): a reps set would turn into a timed one. */
const moveInto = (
  slot: SlotDraft,
  to: SetRowDraft,
  from: SetRowDraft,
  w: number,
  keepNote: boolean,
): SetRowDraft | null => {
  const cell = carry(from.cells[w]!, from, to, w);
  const note = keepNote ? (from.notesPerCell[w] ?? "") : "";
  const raw = keepNote ? from.notesRawPerCell?.[w] : undefined;
  if ((to.workKind ?? "") === (from.workKind ?? "")) return putAt(to, w, cell, note, raw);
  const elsewhere = slot.inactiveWeeks.some((off, i) => i !== w && !off && !isCellAbsent(to, i));
  if (elsewhere) return null;
  const row: SetRowDraft = { ...to };
  if (from.workKind) row.workKind = from.workKind;
  else delete row.workKind;
  return putAt(row, w, cell, note, raw);
};

/** The last rows absent in every round the slot runs in, dropped. The first
 *  row always stays. */
const withoutDeadRows = (slot: SlotDraft): SlotDraft => {
  if (slot.inactiveWeeks.every(Boolean)) return slot;
  const dead = (row: SetRowDraft) =>
    slot.inactiveWeeks.every((off, w) => off || isCellAbsent(row, w));
  let end = slot.setRows.length;
  while (end > 1 && dead(slot.setRows[end - 1]!)) end -= 1;
  return end === slot.setRows.length ? slot : { ...slot, setRows: slot.setRows.slice(0, end) };
};

/** One more set in round `w` only, a copy of its last one, bound
 *  included. A new row, when needed, is absent in every other round the slot
 *  runs in. The same slot when refused: not run there, no set to copy, or
 *  the next row is bound otherwise in another round (moveInto). */
export const addSetAt = (slot: SlotDraft, w: number): SlotDraft => {
  const n = setCountAt(slot, w);
  const prev = slot.setRows[n - 1];
  if (!prev) return slot;
  const rows = slot.setRows;
  const next = rows[n];
  if (next) {
    const filled = moveInto(slot, next, prev, w, false);
    return filled ? { ...slot, setRows: rows.map((r, i) => (i === n ? filled : r)) } : slot;
  }
  const seed = seedNextSetRow(prev);
  const row: SetRowDraft = {
    ...seed,
    cells: seed.cells.map((c, i) => (i === w || slot.inactiveWeeks[i] ? c : absentCell())),
  };
  return { ...slot, setRows: [...rows, row] };
};

/** Set `rowIdx` out of round `w` only; the later sets of that round move up.
 *  null when refused: the round's only set, no such set, or a later set
 *  would move into a row bound otherwise (moveInto). */
export const removeSetAt = (slot: SlotDraft, w: number, rowIdx: number): SlotDraft | null => {
  const n = setCountAt(slot, w);
  if (n <= 1 || rowIdx < 0 || rowIdx >= n) return null;
  const rows: SetRowDraft[] = [];
  for (const [r, row] of slot.setRows.entries()) {
    if (r < rowIdx || r >= n) rows.push(row);
    else if (r === n - 1) rows.push(putAt(row, w, absentCell()));
    else {
      const moved = moveInto(slot, row, slot.setRows[r + 1]!, w, true);
      if (!moved) return null;
      rows.push(moved);
    }
  }
  return withoutDeadRows({ ...slot, setRows: rows });
};

/** One more set in every round (the «Exercises» view): after each round's
 *  last. With every round as long as the rows, a copy of the last row. When
 *  any round refuses, none grows: the same slot. */
export const addSetEveryRound = (slot: SlotDraft): SlotDraft => {
  const last = slot.setRows[slot.setRows.length - 1];
  if (!last) return slot;
  if (slot.inactiveWeeks.every((off, w) => off || !isCellAbsent(last, w)))
    return { ...slot, setRows: [...slot.setRows, seedNextSetRow(last)] };
  // All or nothing: a round that refuses (moveInto) would otherwise be left a
  // set short, silently. The same slot tells the caller nothing was added.
  let grown = slot;
  for (const [w, off] of slot.inactiveWeeks.entries()) {
    if (off) continue;
    const next = addSetAt(grown, w);
    if (next === grown) return slot;
    grown = next;
  }
  return grown;
};

/** Row `rowIdx` out of every round (the «Exercises» view). A round whose
 *  only set it was keeps it: no round is left with no sets. When that set
 *  cannot keep its bound there (moveInto), nothing is removed: the same slot. */
export const removeSetEveryRound = (slot: SlotDraft, rowIdx: number): SlotDraft => {
  const gone = slot.setRows[rowIdx];
  if (!gone || slot.setRows.length <= 1) return slot;
  const kept = slot.setRows.filter((_, i) => i !== rowIdx);
  const rest = { ...slot, setRows: kept };
  let head: SetRowDraft | null = kept[0]!;
  for (const [w, off] of slot.inactiveWeeks.entries()) {
    if (!head) break;
    if (off || !isCellAbsent(head, w) || isCellAbsent(gone, w)) continue;
    head = moveInto(rest, head, gone, w, true);
  }
  return head ? { ...slot, setRows: [head, ...kept.slice(1)] } : slot;
};

/** Q7: a superset stays symmetric round by round. Each member of `groupId`
 *  that runs in a round gets the group's longest count there ("pad", copies
 *  of its own last set) or its shortest ("trim", from the end, never below
 *  one). `{ leader, from }`: the count of slot `leader` wins, in the rounds
 *  from `from` on where it runs (a rule applied to it, slice 5). The one rule
 *  for the editor's join, the builder and the save. Nothing to even out: the
 *  same array. */
export const alignGroupSlots = (
  slots: SlotDraft[],
  groupId: string,
  mode: "pad" | "trim" | { leader: number; from?: number },
): SlotDraft[] => {
  const out = [...slots];
  const idxs = groupMemberIdxs(out, groupId);
  const rounds = Math.max(0, ...idxs.map((i) => out[i]!.inactiveWeeks.length));
  const leader = typeof mode === "object" ? out[mode.leader] : undefined;
  for (let w = typeof mode === "object" ? (mode.from ?? 0) : 0; w < rounds; w += 1) {
    const here = idxs.filter((i) => !out[i]!.inactiveWeeks[w]);
    const counts = here.map((i) => setCountAt(out[i]!, w));
    if (counts.length === 0) continue;
    if (leader && leader.inactiveWeeks[w] !== false) continue;
    const target = leader
      ? setCountAt(leader, w)
      : mode === "pad" ? Math.max(...counts) : Math.max(1, Math.min(...counts));
    for (const i of here) {
      let s = out[i]!;
      while (setCountAt(s, w) < target) {
        const grown = addSetAt(s, w);
        // A refused «+ set» is toasted where it is tapped, so the save is never asked to pad past it.
        if (grown === s) break;
        s = grown;
      }
      while (setCountAt(s, w) > target) {
        const cut = removeSetAt(s, w, setCountAt(s, w) - 1);
        if (!cut) break;
        s = cut;
      }
      out[i] = s;
    }
  }
  // Nothing to even out: the same array, so a caller can tell.
  return out.every((s, i) => s === slots[i]) ? slots : out;
};
